/**
 * Admin: Antragsvorlagen verwalten
 * Create, edit, delete request templates with dynamic field builder and approval chain config
 */
import React, { useState, useEffect, useCallback } from 'react';
import { employeeRequestsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  GraduationCap, ShoppingCart, Plane, Home, Monitor, CreditCard,
  FileCheck, Key, FileText, Plus, Pencil, Trash2, Loader2,
  ChevronRight, GripVertical, X, Settings,
} from 'lucide-react';

const ICON_MAP = {
  GraduationCap, ShoppingCart, Plane, Home, Monitor, CreditCard,
  FileCheck, Key, FileText, Settings,
};
const ICON_OPTIONS = Object.keys(ICON_MAP);

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textbereich' },
  { value: 'number', label: 'Zahl' },
  { value: 'currency', label: 'Betrag (EUR)' },
  { value: 'date', label: 'Datum' },
  { value: 'select', label: 'Auswahl' },
];

const APPROVER_ROLES = [
  { value: 'standortleiter', label: 'Standortleiter/in' },
  { value: 'niederlassungsleiter', label: 'Niederlassungsleiter/in' },
  { value: 'prokurist', label: 'Prokurist/in' },
  { value: 'geschaeftsfuehrer', label: 'Geschaeftsfuehrer/in' },
];

const ROLE_LABELS = Object.fromEntries(APPROVER_ROLES.map(r => [r.value, r.label]));

const CATEGORIES = [
  'Arbeitszeit', 'Beschaffung', 'Facility', 'Finanzen',
  'IT & Ausstattung', 'Personal', 'Weiterbildung', 'Sonstiges',
];

const emptyField = () => ({
  name: '', label: '', field_type: 'text', required: true, options: [], placeholder: '',
});

const emptyForm = () => ({
  name: '', description: '', icon: 'FileText', category: 'Sonstiges',
  fields: [emptyField()], approval_chain: ['standortleiter'], enabled: true,
});

// ========== TEMPLATE EDITOR DIALOG ==========
const TemplateEditorDialog = ({ open, onClose, template, onSaved }) => {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const isEdit = !!template;

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        description: template.description || '',
        icon: template.icon || 'FileText',
        category: template.category || 'Sonstiges',
        fields: (template.fields || []).map(f => ({ ...f, options: f.options || [] })),
        approval_chain: template.approval_chain || [],
        enabled: template.enabled !== false,
      });
    } else {
      setForm(emptyForm());
    }
  }, [template]);

  const updateField = (idx, key, val) => {
    setForm(prev => {
      const fields = [...prev.fields];
      fields[idx] = { ...fields[idx], [key]: val };
      if (key === 'label' && !fields[idx]._nameEdited) {
        fields[idx].name = val.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
      }
      return { ...prev, fields };
    });
  };

  const addField = () => setForm(p => ({ ...p, fields: [...p.fields, emptyField()] }));

  const removeField = (idx) => {
    setForm(p => ({ ...p, fields: p.fields.filter((_, i) => i !== idx) }));
  };

  const toggleChainRole = (role) => {
    setForm(prev => {
      const chain = prev.approval_chain.includes(role)
        ? prev.approval_chain.filter(r => r !== role)
        : [...prev.approval_chain, role];
      return { ...prev, approval_chain: chain };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    if (form.fields.some(f => !f.label.trim())) { toast.error('Alle Felder brauchen ein Label'); return; }
    if (form.approval_chain.length === 0) { toast.error('Mindestens eine Genehmigungsstufe erforderlich'); return; }

    const payload = {
      ...form,
      fields: form.fields.map(f => ({
        name: f.name || f.label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        label: f.label,
        field_type: f.field_type,
        required: f.required,
        options: f.field_type === 'select' ? (f.options || []) : [],
        placeholder: f.placeholder || '',
      })),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await employeeRequestsAPI.updateTemplate(template.id, payload);
        toast.success('Vorlage aktualisiert');
      } else {
        await employeeRequestsAPI.createTemplate(payload);
        toast.success('Vorlage erstellt');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}</DialogTitle>
          <DialogDescription>Definieren Sie die Felder und Genehmigungskette</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="z.B. Parkplatz beantragen"
                data-testid="tpl-name"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Beschreibung</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Kurze Beschreibung des Antrags..."
                data-testid="tpl-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Kategorie</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="tpl-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Icon</Label>
              <Select value={form.icon} onValueChange={v => setForm(p => ({ ...p, icon: v }))}>
                <SelectTrigger data-testid="tpl-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map(ic => {
                    const Ic = ICON_MAP[ic];
                    return (
                      <SelectItem key={ic} value={ic}>
                        <span className="flex items-center gap-2"><Ic className="h-4 w-4" />{ic}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Fields builder */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Formularfelder</Label>
              <Button variant="outline" size="sm" onClick={addField} data-testid="btn-add-field">
                <Plus className="h-3 w-3 mr-1" /> Feld hinzufuegen
              </Button>
            </div>
            <div className="space-y-3">
              {form.fields.map((field, idx) => (
                <div key={field.id || `field-${idx}`} className="border rounded-lg p-3 space-y-2 bg-muted/30" data-testid={`field-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                      className="flex-1"
                      placeholder="Feldbezeichnung"
                      value={field.label}
                      onChange={e => updateField(idx, 'label', e.target.value)}
                      data-testid={`field-label-${idx}`}
                    />
                    <Select value={field.field_type} onValueChange={v => updateField(idx, 'field_type', v)}>
                      <SelectTrigger className="w-36" data-testid={`field-type-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(ft => (
                          <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1">
                      <Checkbox
                        checked={field.required}
                        onCheckedChange={v => updateField(idx, 'required', v)}
                        id={`req-${idx}`}
                      />
                      <Label htmlFor={`req-${idx}`} className="text-xs">Pflicht</Label>
                    </div>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-red-500 hover:text-red-700"
                      onClick={() => removeField(idx)}
                      disabled={form.fields.length <= 1}
                      data-testid={`btn-remove-field-${idx}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {field.field_type === 'select' && (
                    <div className="pl-6 space-y-1">
                      <Label className="text-xs text-muted-foreground">Optionen (kommagetrennt)</Label>
                      <Input
                        placeholder="Option 1, Option 2, Option 3"
                        value={(field.options || []).join(', ')}
                        onChange={e => updateField(idx, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                        data-testid={`field-options-${idx}`}
                      />
                    </div>
                  )}
                  <div className="pl-6">
                    <Input
                      className="text-xs"
                      placeholder="Platzhalter-Text (optional)"
                      value={field.placeholder || ''}
                      onChange={e => updateField(idx, 'placeholder', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approval chain */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Genehmigungskette <span className="text-red-500">*</span></Label>
            <p className="text-xs text-muted-foreground">Waehlen Sie die Genehmigungsstufen in der gewuenschten Reihenfolge</p>
            <div className="space-y-2">
              {APPROVER_ROLES.map(role => (
                <div
                  key={role.value}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    form.approval_chain.includes(role.value)
                      ? 'border-primary bg-primary/5'
                      : 'border-border/60 hover:border-border'
                  }`}
                  onClick={() => toggleChainRole(role.value)}
                  data-testid={`chain-role-${role.value}`}
                >
                  <Checkbox
                    checked={form.approval_chain.includes(role.value)}
                    onCheckedChange={() => toggleChainRole(role.value)}
                  />
                  <span className="text-sm font-medium">{role.label}</span>
                  {form.approval_chain.includes(role.value) && (
                    <Badge variant="outline" className="ml-auto text-xs">
                      Stufe {form.approval_chain.indexOf(role.value) + 1}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            {form.approval_chain.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1">
                Reihenfolge: {form.approval_chain.map((r, i) => (
                  <React.Fragment key={r}>
                    {i > 0 && <ChevronRight className="h-3 w-3" />}
                    <span className="font-medium">{ROLE_LABELS[r]}</span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Enabled toggle */}
          <div className="flex items-center gap-3 rounded-lg border p-3">
            <Switch
              checked={form.enabled}
              onCheckedChange={v => setForm(p => ({ ...p, enabled: v }))}
              data-testid="tpl-enabled"
            />
            <div>
              <p className="text-sm font-medium">Vorlage aktiv</p>
              <p className="text-xs text-muted-foreground">Deaktivierte Vorlagen sind fuer Mitarbeiter nicht sichtbar</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-template">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Speichern' : 'Vorlage erstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========== MAIN COMPONENT ==========
export const RequestTemplateManager = ({ onTemplatesChanged }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeRequestsAPI.getTemplates({ include_disabled: true });
      setTemplates(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleEdit = (tpl) => {
    setEditingTemplate(tpl);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const handleDelete = async (tpl) => {
    try {
      await employeeRequestsAPI.deleteTemplate(tpl.id);
      toast.success('Vorlage geloescht');
      loadTemplates();
      if (onTemplatesChanged) onTemplatesChanged();
      setDeleteConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Loeschen');
    }
  };

  const handleSaved = () => {
    loadTemplates();
    if (onTemplatesChanged) onTemplatesChanged();
  };

  // Group by category
  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'Sonstiges';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="template-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{templates.length} Vorlagen insgesamt</p>
        <Button onClick={handleCreate} data-testid="btn-create-template">
          <Plus className="h-4 w-4 mr-2" /> Neue Vorlage
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, tpls]) => (
            <div key={category}>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{category}</h3>
              <div className="space-y-2">
                {tpls.map(tpl => {
                  const IconComp = ICON_MAP[tpl.icon] || FileText;
                  return (
                    <Card
                      key={tpl.id}
                      className={`transition-colors ${!tpl.enabled ? 'opacity-50' : ''}`}
                      data-testid={`admin-tpl-${tpl.id}`}
                    >
                      <CardContent className="p-3 flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                          <IconComp className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{tpl.name}</p>
                            {!tpl.enabled && <Badge variant="outline" className="text-xs">Deaktiviert</Badge>}
                            {tpl.is_custom && <Badge className="bg-blue-500 text-white text-xs">Eigene</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-1">{tpl.description}</p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <span className="text-[10px] text-muted-foreground">Kette:</span>
                            {(tpl.approval_chain || []).map((role, i) => (
                              <React.Fragment key={role}>
                                {i > 0 && <ChevronRight className="h-2.5 w-2.5 text-muted-foreground" />}
                                <span className="text-[10px] text-muted-foreground font-medium">
                                  {ROLE_LABELS[role] || role}
                                </span>
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Badge variant="outline" className="text-xs">
                            {(tpl.fields || []).length} Felder
                          </Badge>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => handleEdit(tpl)}
                            data-testid={`btn-edit-tpl-${tpl.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => setDeleteConfirm(tpl)}
                            data-testid={`btn-delete-tpl-${tpl.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateEditorDialog
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditingTemplate(null); }}
        template={editingTemplate}
        onSaved={handleSaved}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Vorlage loeschen?</DialogTitle>
            <DialogDescription>
              Moechten Sie "{deleteConfirm?.name}" wirklich loeschen? Bestehende Antraege bleiben erhalten.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="btn-confirm-delete">
              Loeschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
